import { useState, Fragment, useEffect } from 'react';
import { useAppSelector } from '../../../../store/configureStore';

import { DyePenetrantInspectionProps, PDFImageProps, RTPSummaryData, RTWCoverData, RTWRadiographyData, RTWRadiographySummaryData, VTEquipmentAnomalyData } from '../../../../utils/models';
import { tutorialConstants } from '../../../../utils/constants';
import { getTutorials } from '../../../../store/tutorials';

import Button from '../../../themed/button';
import DyeInspectionItem from '../../../forms/dpt/dyeInspectionItem';
import Elevation from '../../../themed/instruction-modals/elevation';
import ETAnomalySummary from '../et/etAnomalySummary';
import Modal from '../../../themed/modal';
import RTWReport from '.';
import RTPhotograpicRecord from '../../../forms/rt-weld/rtPhotographicRecord';
import RTWRadiographySummary from '../../../forms/rt-weld/rtwRadiographySummary';
import RTIsoPageForm from '../../../forms/rt-weld/rtIsoPageForm';
import RTWCoverForm from '../../../forms/rt-weld/rtwCoverForm';
import RTWSummaryOverviewForm from '../../../forms/rt-weld/rtSummaryOverviewForm';
import RTWRadiographyForm from '../../../forms/rt-weld/rtwRadiographyForm';
import Zippy from '../../../themed/zippy';

import useAutoSaveReport from '../../../../hooks/useAutoSave';
import useProjectDetails from '../../../../hooks/useProjectDetails';

const CreateRTWReport = () => {
	const [coverData, setCoverData] = useState<RTWCoverData>();
	const [summaryData, setSummaryData] = useState<RTPSummaryData>();
	const [anomalyData, setAnomalyData] = useState<VTEquipmentAnomalyData[]>([]);
	const [photoData, setPhotoData] = useState<PDFImageProps[]>([]);
	const [isoData, setIsoData] = useState<PDFImageProps[]>([]);
	const [radiographyData, setRadiographyData] = useState<RTWRadiographyData>();
	const [previewReport, setPreviewReport] = useState<boolean>(false);
	const [lptData, setLptData] = useState<DyePenetrantInspectionProps>(initialLptData);
	const [radiographySummary, setRadiographySummary] = useState<RTWRadiographySummaryData>();

	const togglePreviewReport = (): void => {
		setPreviewReport((p: boolean) => !p);
	};

	const getReportDetails = () => {
		return {
			coverSheetData: coverData,
			summarySheetData: summaryData,
			anomalyData,
			lptData,
			isoData,
			projectDetails,
			photographicRecord: photoData,
			radiographyData,
			radiographySummary,
		};
	};

	const handleSaveAndPreview = () => {
		autoSave.saveReport();
		togglePreviewReport();
	};

	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);

	const reportDetails = getReportDetails();
	const autoSave = useAutoSaveReport(reportDetails);

	useEffect(() => {
		type ReportData = ReturnType<typeof getReportDetails>;
		const cachedReport = autoSave.oldReportData as ReportData | null;

		if (cachedReport) {
			setAnomalyData(cachedReport.anomalyData);
			setCoverData(cachedReport.coverSheetData);
			setSummaryData(cachedReport.summarySheetData);
			setPhotoData(cachedReport.photographicRecord);
			setIsoData(cachedReport.isoData);
			setRadiographyData(cachedReport.radiographyData);
			setLptData(cachedReport.lptData);
			setRadiographySummary(cachedReport.radiographySummary);
		}
	}, [autoSave.oldReportData]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<RTWReport {...reportDetails} />
			</Modal>

			<Zippy headerTitle="Cover Sheet" isValid={!!coverData}>
				<RTWCoverForm onProceed={setCoverData} />
			</Zippy>

			<Zippy headerTitle="Summary Sheet" isValid={!!summaryData}>
				<RTWSummaryOverviewForm onProceed={setSummaryData} />
			</Zippy>

			<Zippy headerTitle="Isometric Drawing Sheet" isValid={isoData.length > 0}>
				<RTIsoPageForm onProceed={setIsoData} />
			</Zippy>

			<Zippy
				headerTitle="Liquid Penetrant Testing"
				isValid={lptData.data.length > 0}
			>
				<DyeInspectionItem onAdd={setLptData} />
			</Zippy>

			<Zippy
				headerTitle="Digital Radiography Overview Data"
				isValid={!!radiographyData}
			>
				<RTWRadiographyForm onProceed={setRadiographyData} />
			</Zippy>

			<Zippy
				headerTitle="Digital Radiography Summary Data"
				isValid={!!radiographySummary}
			>
				<RTWRadiographySummary onProceed={setRadiographySummary} />
			</Zippy>

			<Zippy headerTitle="Anomaly Report" isValid={anomalyData.length > 0}>
				<ETAnomalySummary
					anomalyData={anomalyData}
					onDataChange={setAnomalyData}
				/>
			</Zippy>

			<Zippy headerTitle="Photographic Record" isValid={photoData.length > 0}>
				<RTPhotograpicRecord onProceed={setPhotoData} />
			</Zippy>

			<Elevation
				isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}
			>
				<div className="d-flex justify-content-end">
					<Button
						label="Save and Preview Report"
						disabled={error}
						onClick={handleSaveAndPreview}
					/>
				</div>
			</Elevation>
		</Fragment>
	);
};

const initialLptData: any = {
	data: [],
};

export default CreateRTWReport;
